import React, { useEffect, useState } from "react";
import YearPager from "../lists/common/YearPager";
import SortSelect from "../lists/common/SortSelect";
import OrderModal from "../lists/common/OrderModal";
import "../../../assets/styles/common/listCard.scss";
import { GroupData } from "@/types/GroupData";

interface GroupListsProps {
  groups: GroupData[];
  loading: boolean;
  onEdit: (groupId: string) => void;
  selectedTab: string;
  currentYear: number;
  setCurrentYear: React.Dispatch<React.SetStateAction<number>>;
  onGroupsChanged?: () => void;
}

const GroupLists: React.FC<GroupListsProps> = ({
  groups,
  loading,
  onEdit,
  selectedTab,
  currentYear,
  setCurrentYear,
  onGroupsChanged,
}) => {
  const [modalOpen, setModalOpen] = useState(false);

  const [sortOption, setSortOption] = useState(() => {
    return localStorage.getItem("sortOption") || "entryNumber";
  });

  useEffect(() => {
    localStorage.setItem("currentYear", String(currentYear));
  }, [currentYear]);

  useEffect(() => {
    localStorage.setItem("sortOption", sortOption);
  }, [sortOption]);

  // createdAt で年ごとに絞り込み
  const minYear = 2024;
  const maxYear = new Date().getFullYear();

  const filteredGroups = groups.filter((group) => {
    if (group.isDeleted === true) return false;

    if (group.entryClass !== selectedTab) return false;

    const createdAt = group.createdAt?.toDate();

    return createdAt?.getFullYear() === currentYear;
  });

  const sortOptions = [
    { value: "entryNumber", label: "エントリーNo.順" },
    { value: "groupName", label: "グループ名順" },
    { value: "createdAt", label: "応募日付順" },
  ];

  const sortedGroups = [...filteredGroups].sort((a, b) => {
    if (sortOption === "entryNumber") {
      return Number(a.entryNumber ?? 0) - Number(b.entryNumber ?? 0);
    }

    if (sortOption === "createdAt") {
      const aTime = a.createdAt?.toDate().getTime() ?? 0;
      const bTime = b.createdAt?.toDate().getTime() ?? 0;

      return aTime - bTime;
    }

    if (sortOption === "groupName") {
      return (a.groupName ?? "").localeCompare(
        b.groupName ?? "",
        "ja",
      );
    }

    return 0;
  });

  // 曲情報入力チェック
  const isMusicInfoComplete = (group: GroupData): boolean => {
    const fieldsToCheck =
      group.entryClass === "ChorusContest" ||
      group.entryClass === "EvaluationOnly"
        ? [
            group.songArranged1,
            group.songArranged2,
            group.songComposed1,
            group.songComposed2,
            group.songLyrics1,
            group.songLyrics2,
            group.songName1,
            group.songName2,
          ]
        : [
            group.songName1,
            group.songLyrics1,
            group.songComposed1,
            group.songArranged1,
            group.songName2,
            group.songLyrics2,
            group.songComposed2,
            group.songArranged2,
            group.songName3,
            group.songLyrics3,
            group.songComposed3,
            group.songArranged3,
            group.songName4,
            group.songLyrics4,
            group.songComposed4,
            group.songArranged4,
          ];

    return fieldsToCheck.every(
      (field) => field && field.trim() !== "",
    );
  };

  if (loading) {
    return (
      <div className="table-contents">
        <div className="table-main">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="table-contents">
      <div className="table-main">
        <div className="table-header">
          <YearPager
            currentYear={currentYear}
            minYear={minYear}
            maxYear={maxYear}
            setCurrentYear={setCurrentYear}
          />

          <div className="flex">
            <div
              className="sub-btn"
              onClick={() => setModalOpen(true)}
            >
              Semifinal Order
            </div>

            <OrderModal
              isOpen={modalOpen}
              onClose={() => setModalOpen(false)}
              currentYear={currentYear}
              type="semifinal"
              selectedTab={selectedTab}
              onSaved={onGroupsChanged}
            />

            <SortSelect
              sortOption={sortOption}
              setSortOption={setSortOption}
              options={sortOptions}
            />
          </div>
        </div>

        {sortedGroups.map((group) => (
          <section className="card-list" key={group.id}>
            <article className="list-card">
              <div className="flex-space-between">
                <div className="flex-align-center">
                  <p className="entry-number">
                    {group.entryNumber}
                  </p>

                  <h2>{group.groupName}</h2>

                  <div className="flex-align-center member">
                    {selectedTab === "QuartetContest" && (
                      <>
                        <p>
                          {group.quartetContestMemberTenor}
                        </p>
                        <p>
                          {group.quartetContestMemberLead}
                        </p>
                        <p>
                          {group.quartetContestMemberBaritone}
                        </p>
                        <p>
                          {group.quartetContestMemberBass}
                        </p>
                      </>
                    )}

                    {(selectedTab === "ChorusContest" ||
                      selectedTab === "EvaluationOnly") && (
                      <p className="chorus-contest-attendance">
                        {group.chorusContestAttendance}
                      </p>
                    )}

                    {selectedTab === "ChorusContest" && (
                      <p className="chorus-director">
                        {group.chorusDirector}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <p className="updated-at">
                    {group.updatedAt
                      ? group.updatedAt
                          .toDate()
                          .toLocaleDateString("ja-JP", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                          })
                      : "未設定"}
                  </p>

                  <div className="list-edit">
                    <button
                      className="editbtn"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(group.id);
                      }}
                    >
                      詳細
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex check-list">
                <div className="isjoin-competition">
                  <div
                    className={
                      group.joinChampionship
                        ? "check confirmed"
                        : "check unconfirmed"
                    }
                  >
                    <p>
                      Championship
                      {group.joinChampionship
                        ? "参加"
                        : "不参加"}
                    </p>
                  </div>

                  <div
                    className={
                      group.joinPreliminary
                        ? "check confirmed"
                        : "check unconfirmed"
                    }
                  >
                    <p>
                      Preliminary
                      {group.joinPreliminary
                        ? "参加"
                        : "不参加"}
                    </p>
                  </div>
                </div>

                <div
                  className={
                    isMusicInfoComplete(group)
                      ? "check confirmed"
                      : "check unconfirmed"
                  }
                >
                  <span></span>
                  <p>演奏情報</p>
                </div>

                <div
                  className={
                    group.scoreFile
                      ? "check confirmed"
                      : "check unconfirmed"
                  }
                >
                  <span></span>
                  <p>楽譜</p>
                </div>

                <div
                  className={
                    group.performLicense
                      ? "check confirmed"
                      : "check unconfirmed"
                  }
                >
                  <span></span>
                  <p>演奏許可証</p>
                </div>

                <div
                  className={
                    group.inviteChat
                      ? "check confirmed"
                      : "check unconfirmed"
                  }
                >
                  <span></span>
                  <p>LINE招待</p>
                </div>
              </div>

              <div className="memo">
                <p>{group.information}</p>
              </div>
            </article>
          </section>
        ))}
      </div>
    </div>
  );
};

export default GroupLists;